'use strict';
import isFunction from 'lodash/isFunction';
import isNumber from 'lodash/isNumber';
import * as T from 'prop-types';
import React from 'react';
import {componentFromStream, createEventHandler} from 'recompose';
import {Observable} from 'rxjs/Observable';
import {INTERVAL} from '../../../auth/services/UserPresenceService';
import {MINUTE} from '../../../timers/utils';
import {PeopleItemView} from './PeopleItemView';
import {PeopleItemViewSimple} from './PeopleItemViewSimple';


const ANIMATION_DURATION = 1000;

export const PeopleItemComponent = componentFromStream(props$ => {
  const {
    handler: onClick,
    stream: click$,
  } = createEventHandler();

  click$
    .withLatestFrom(props$)
    .takeUntil(props$.last())
    .subscribe(([e, props]) => {
      if (isFunction(props.onClick)) {
        e.stopPropagation();
        props.onClick(e, props.model);
      }
    });

  const presenceState$ = props$
    .distinctUntilChanged(null, hasPresenceChanged)
    .switchMap(onPresenceChange)
    .distinctUntilChanged(comparePresenceState);

  return props$
    .combineLatest(
      presenceState$,
      (props, presenceState) => {
        const useListItem = isFunction(props.onClick);
        const model = getModel(props.model, props.isOption);
        if (!props.isSimple) {
          return (
            <PeopleItemView
              className={props.className}
              isUser={presenceState.isUser}
              isOnline={presenceState.isOnline}
              isInactive={presenceState.isInactive}
              pulseOnline={presenceState.pulseOnline}
              isSelected={props.isSelected}
              nullLabel={props.nullLabel}
              model={model}
              onClick={onClick}
              onContextMenu={props.onContextMenu}
            />
          );
        } else {
          return (
            <PeopleItemViewSimple
              className={props.className}
              isUser={presenceState.isUser}
              isOnline={presenceState.isOnline}
              isInactive={presenceState.isInactive}
              pulseOnline={presenceState.pulseOnline}
              isSelected={props.isSelected}
              rtl={props.rtl}
              showName={props.showName}
              nullLabel={props.nullLabel}
              model={model}
              onClick={onClick}
              useListItem={useListItem}
              avatarImageWidth={props.avatarImageWidth}
              avatarImageHeight={props.avatarImageHeight}
            />
          );
        }
      }
    );
});

PeopleItemComponent.propTypes = {
  className: T.string,
  isOptions: T.bool,
  rtl: T.bool,
  showName: T.bool,
  isSimple: T.bool,
  isSelected: T.bool,
  nullLabel: validateName,
  model: T.object,
  onClick: T.func,
  onContextMenu: T.func,
  avatarImageWidth: T.number,
  avatarImageHeight: T.number,
};

PeopleItemComponent.defaultProps = {
  rtl: false,
  isSimple: false,
  showName: true,
  avatarImageWidth: null,
  avatarImageHeight: null,
};

function getModel(model, isOption) {
  return isOption ? model.value : model;
}

function hasPresenceChanged(props) {
  const model = getModel(props.model, props.isOption);
  return model ? `${model.lastOnline}, ${model.lastSeen}` : null;
}

function hasIsUserChanged(props) {
  return isUser(getModel(props.model, props.isOption));
}

function onPresenceChange(props) {
  if (!isUser(getModel(props.model, props.isOption))) {
    return Observable.of({
      isUser: false,
      isOnline: false,
      isInactive: false,
      pulseOnline: false,
    });
  } else {
    return Observable.interval(0, MINUTE)
      .distinctUntilChanged(null, hasIsUserChanged)
      .switchMap(() => getPresenceState$(getModel(props.model, props.isOption)));
  }
}

function getPresenceState$(person) {
  if (!isUser(person)) {
    return Observable.of({
      isUser: false,
      isOnline: false,
      isInactive: false,
      pulseOnline: false,
    });
  } else {
    const lastOnline = person.lastOnline;
    const lastSeen = person.lastSeen;
    const now = new Date().getTime() - INTERVAL;
    if (!isNumber(lastOnline) || !isNumber(lastSeen)) {
      return Observable.of({
        isUser: true,
        isOnline: false,
        isInactive: false,
        pulseOnline: false,
      });
    } else if (now < lastOnline) {
      return Observable.merge(
        Observable.of({
          isUser: true,
          isOnline: true,
          isInactive: false,
          pulseOnline: true,
        }),
        Observable.timer(ANIMATION_DURATION)
          .take(1)
          .mapTo({
            isUser: true,
            isOnline: true,
            isInactive: false,
            pulseOnline: false,
          })
      );
    } else {
      return Observable.of({
        isUser: true,
        isOnline: false,
        isInactive: now < lastSeen,
        pulseOnline: false,
      });
    }
  }
}

function isUser(person) {
  return person && (person.aliasFor || person.user);
}

/**
 * @param {PeopleItemComponent~PresenceState} a
 * @param {PeopleItemComponent~PresenceState} b
 * @return {boolean}
 */
function comparePresenceState(a, b) {
  return a.isUser === b.isUser
    && a.isOnline === b.isOnline
    && a.isInactive === b.isInactive
    && a.pulseOnline === b.pulseOnline
  ;
}

/**
 * @typedef {Object} PeopleItemComponent~PresenceState
 * @property {Boolean} isUser
 * @property {Boolean} isOnline
 * @property {Boolean} isInactive
 * @property {Boolean} pulseOnline
 */

function validateName(props) {
  const model = props.model || {};
  const hasValidName = model.name || props.nullLabel;

  if (props.showName && !hasValidName) {
    return new Error(`Missing required nullLabel in PeopleItem`);
  } else {
    return null;
  }
}



// WEBPACK FOOTER //
// ./src/js/personnel/components/PeopleItem/PeopleItemComponent.js